import { useState } from 'react';
import { useTransferRequests } from '@/hooks/useTransferRequest';
import { Spinner, EmptyState, Pagination } from '@/components/common';
import { cn } from '@/lib/utils';
import type { TransferRequestFilter } from '@/types';
import TransferRequestCard from '@/components/card/TransferRequestCard';
import { Select, MenuItem } from '@mui/material';
import { TransferFilter } from '@/components/transfer/TransferFilter';

const LIMIT = 12;

export default function TransferRequestListPage() {
  const [filters, setFilters] = useState<TransferRequestFilter>({
    page: 1,
    limit: LIMIT,
    orderBy: 'createdAt',
    orderDirection: 'DESC',
  });

  const { data, isLoading, isFetching } = useTransferRequests(filters);

  const handleFilterSearch = (searchFilters: any) => {
    setFilters((prev) => {
      // Map price filters to budget filters for Transfer Requests
      const mappedFilters = {
        ...searchFilters,
        minBudget: searchFilters.minPrice,
        maxBudget: searchFilters.maxPrice,
      };
      
      // Clean up fields not relevant to transfer requests
      delete mappedFilters.minPrice;
      delete mappedFilters.maxPrice;
      delete mappedFilters.minRevenue;
      delete mappedFilters.maxRevenue;
      delete mappedFilters.minProfit;
      delete mappedFilters.maxProfit;
      delete mappedFilters.businessSectorId; 

      const newFilters = { ...prev, ...mappedFilters, page: 1 };
      
      const cleanFilters = Object.fromEntries(
        Object.entries(newFilters).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );
      
      return {
        ...cleanFilters,
        page: 1,
        limit: LIMIT,
        orderBy: prev.orderBy || 'createdAt',
        orderDirection: prev.orderDirection || 'DESC',
      } as TransferRequestFilter;
    });
  };

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="pb-20 animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Sang nhượng</div>
        <div className="flex items-end justify-between">
          <h1 className="section-title mb-0">Nhu cầu mua sang nhượng</h1>
          {total > 0 && (
            <span className="text-sm font-semibold" style={{ color: 'var(--text-3)' }}>
              {total} kết quả
            </span>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sticky top-[104px] z-30 mb-8">
        <TransferFilter 
          onSearch={handleFilterSearch} 
          initialFilters={{
            ...filters,
            minPrice: filters.minBudget,
            maxPrice: filters.maxBudget,
          }} 
        />
      </div>

      <div className="flex justify-end mb-4">
        <Select
          value={`${filters.orderBy || 'createdAt'}|${filters.orderDirection || 'DESC'}`}
          onChange={(e) => {
            const [orderBy, orderDirection] = (e.target.value as string).split('|') as [TransferRequestFilter['orderBy'], 'ASC' | 'DESC'];
            setFilters(prev => ({ ...prev, orderBy, orderDirection, page: 1 }));
          }}
          size="small"
          sx={{
            minWidth: '160px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            }
          }}
        >
          <MenuItem value="createdAt|DESC" sx={{ fontSize: '14px' }}>Mới nhất</MenuItem>
          <MenuItem value="budgetMin|ASC" sx={{ fontSize: '14px' }}>Ngân sách tăng dần</MenuItem>
          <MenuItem value="budgetMin|DESC" sx={{ fontSize: '14px' }}>Ngân sách giảm dần</MenuItem>
        </Select>
      </div>

      {/* ── Results ── */}
      {isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <EmptyState title="Không tìm thấy nhu cầu" description="Thử thay đổi bộ lọc hoặc tìm kiếm khác" icon="🔍" />
      ) : (
        <>
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isFetching && 'opacity-60')}>
            {items.map((item) => (
              <TransferRequestCard key={item.id} item={item} />
            ))}
          </div>

          {/* Pagination */}
          <Pagination page={filters.page ?? 1} totalPages={totalPages} onChange={(p) => setFilters((f) => ({ ...f, page: p }))} />
        </>
      )}
    </div>
  );
}
